import { z } from 'zod/v4';

export const PAYMENT_METHODS = ['cash', 'transfer', 'check', 'draft', 'card'] as const;

export function requiresBankAccount(paymentMethod: (typeof PAYMENT_METHODS)[number]): boolean {
  return paymentMethod === 'draft';
}

export const paymentSchema = z
  .object({
    paymentType: z.enum(['receipt', 'disbursement']),
    paymentMethod: z.enum(PAYMENT_METHODS),
    thirdPartyType: z.enum(['client', 'supplier', 'other']),
    thirdPartyId: z.string().min(1, 'payments.validation.thirdPartyRequired'),
    bankAccountId: z.string().default(''),
    amount: z.coerce.number().positive('payments.validation.amountPositive'),
    paymentDate: z.string().min(1, 'payments.validation.dateRequired'),
    reference: z.string().default(''),
    notes: z.string().default(''),
    draftNumber: z.string().default(''),
    draftDueDate: z.string().default(''),
    draftPlaceOfIssue: z.string().default(''),
    draftPlaceOfPayment: z.string().default(''),
    draftProtestable: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (requiresBankAccount(data.paymentMethod) && !data.bankAccountId) {
      ctx.addIssue({
        code: 'custom',
        path: ['bankAccountId'],
        message: 'checks.validation.bankAccountRequired',
      });
    }
    if (data.paymentMethod === 'draft' && !data.draftNumber.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['draftNumber'],
        message: 'checks.validation.numberRequired',
      });
    }
    if (data.paymentMethod === 'draft' && !data.draftDueDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['draftDueDate'],
        message: 'checks.validation.invalidDate',
      });
    }
    if (data.paymentMethod === 'draft' && data.thirdPartyType === 'other') {
      ctx.addIssue({
        code: 'custom',
        path: ['thirdPartyId'],
        message: 'checks.validation.thirdPartyRequired',
      });
    }
  });

export type PaymentFormData = z.infer<typeof paymentSchema>;
